import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { logger } from '#app/services/logger.js';
import { MODAL_ANALYTICS_NAMES } from './modalImportDefinitions.js';
import {
  type AdditionalTrackingProperties,
  type ModalArgs,
  type ModalTemplateKeys,
  type UseModalReturn,
} from './types.js';
import { useGlobalModalContext } from './useGlobalModalContext.js';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';

const log = logger.child({
  namespace: 'GlobalModalSystem.useModal',
});

type UseModalProps<K extends ModalTemplateKeys> = {
  identifier: K;
  modalProps: ModalArgs<K>;
  staticAdditionalTrackingProperties?: AdditionalTrackingProperties;
};
/**
 * The useModal hook is used to control any modal template defined in the V2 global modal system. See the readme for instructions on adding new modal templates.
 * @param props
 * @param props.identifier - The identifier of the modal to be opened ex: SampleRegularModal
 * @param props.modalProps - The props to be passed to the modal, typed based on the provided modal identifier
 * @param props.staticAdditionalTrackingProperties - Additional properties to be passed to the modal viewed event
 * @returns isModalOpen - A boolean indicating whether the modal is open or not
 * @returns openModal - A function to open the modal. Can optionally take additionalTrackingProperties to be passed to the modal viewed event
 * @returns closeModal - A function to close the modal
 */
export const useModal = <K extends ModalTemplateKeys>({
  identifier,
  modalProps,
  staticAdditionalTrackingProperties,
}: UseModalProps<K>): UseModalReturn => {
  const { track } = useAnalytics();
  const hasOpenedOnce = useRef(false);
  const modalName = MODAL_ANALYTICS_NAMES[identifier];

  const modalId = useId();

  const {
    activeModalId,
    activeModalName,
    closeModal,
    isOpen,
    openModal,
    setActiveModalId,
    setActiveModalName,
    setActiveModalProps,
  } = useGlobalModalContext();

  const isModalActive =
    modalId === activeModalId && activeModalName === identifier;
  const isModalOpen = isModalActive && isOpen;

  const trackModalViewed = useCallback(
    (additionalTrackingProperties?: AdditionalTrackingProperties) => {
      track('modal', {
        action: 'viewed',
        modal_name: modalName,
        ...staticAdditionalTrackingProperties,
        ...additionalTrackingProperties,
      });
    },
    [modalName, staticAdditionalTrackingProperties, track],
  );

  // if the data updates while the modal is active, update the modal props
  useEffect(() => {
    if (isModalActive) {
      setActiveModalProps(modalProps);
    }
    // only want to update the Context's state when our modal is active and its props update
  }, [modalProps]);

  const openAndTrackModalViewed = useCallback(
    (additionalTrackingProperties?: AdditionalTrackingProperties) => {
      if (activeModalName !== null && !isModalActive && isOpen) {
        log.info(
          `A modal ${activeModalName} is already open, overwriting with ${identifier}`,
        );
      }

      setActiveModalName(identifier);
      setActiveModalProps(modalProps);
      setActiveModalId(modalId);

      openModal();

      if (!hasOpenedOnce.current) {
        trackModalViewed(additionalTrackingProperties);
        hasOpenedOnce.current = true;
      }
    },
    [
      activeModalName,
      identifier,
      isModalActive,
      isOpen,
      modalId,
      modalProps,
      openModal,
      setActiveModalId,
      setActiveModalName,
      setActiveModalProps,
      trackModalViewed,
    ],
  );

  return useMemo(() => {
    return {
      closeModal,
      isModalOpen,
      openModal: openAndTrackModalViewed,
    };
  }, [closeModal, isModalOpen, openAndTrackModalViewed]);
};
